'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Item } from '@radix-ui/react-accordion';
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';

import { Plus } from '@/components/icons/plus';
import { cn } from '@/utils/cn';

const Accordion = AccordionPrimitive.Root;

Accordion.displayName = 'Accordion';

const AccordionItem = forwardRef<
    ElementRef<typeof Item>,
    ComponentPropsWithoutRef<typeof Item>
>(({ className, ...props }, ref) => (
    <AccordionPrimitive.Item
        ref={ref}
        className={cn('group', className)}
        {...props}
    />
));
AccordionItem.displayName = 'AccordionItem';

type AccordionTriggerProps = ComponentPropsWithoutRef<
    typeof AccordionPrimitive.Trigger
> & {
    disablePlusButton?: boolean;
};

const AccordionTrigger = forwardRef<
    ElementRef<typeof AccordionPrimitive.Trigger>,
    AccordionTriggerProps
>(({ children, className, disablePlusButton, ...props }, ref) => (
    <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
            ref={ref}
            className={cn(
                `
                  typography-sub-heading flex flex-1 items-center
                  justify-between py-4 text-left font-bold transition-all
                `,
                className,
            )}
            {...props}
        >
            {children}
            {!disablePlusButton && (
                <Plus
                    className={`
                      ml-6 size-8 shrink-0 rounded-full bg-primary-white
                      stroke-1 text-primary-black outline-primary-white
                      transition-transform duration-200

                      group-data-[state=open]:rotate-45
                      group-data-[state=open]:bg-primary-black
                      group-data-[state=open]:stroke-primary-white
                      group-data-[state=open]:outline
                      group-data-[state=open]:outline-1

                      group-hover:group-data-[state=closed]:bg-primary-limegreen
                      group-hover:group-data-[state=closed]:stroke-2
                      group-hover:group-data-[state=open]:stroke-2
                      group-hover:group-data-[state=open]:outline-2
                    `}
                />
            )}
        </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = forwardRef<
    ElementRef<typeof AccordionPrimitive.Content>,
    ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ children, className, ...props }, ref) => (
    <AccordionPrimitive.Content
        ref={ref}
        className={`
          typography-body2 overflow-hidden transition-all

          data-[state=closed]:animate-accordion-up

          data-[state=open]:animate-accordion-down
        `}
        {...props}
    >
        <div className={cn('pb-4 pt-0', className)}>{children}</div>
    </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
